import React from 'react'
import { motion } from 'framer-motion'
import logo from '../assets/logo.png'
import aboutImage from '../assets/about.png'
import { 
    HiShieldCheck, 
    HiHeart, 
    HiLightningBolt, 
    HiCheckCircle,
    HiOfficeBuilding,
    HiUserGroup,
    HiCube,
    HiGlobe
} from 'react-icons/hi'

export default function About() {
    const coreValues = [
        {
            icon: HiShieldCheck,
            title: 'Quality',
            description: 'Rigorous quality assurance and compliance with international medical standards',
            color: 'from-medical-blue-500 to-medical-blue-600'
        },
        {
            icon: HiHeart,
            title: 'Integrity',
            description: 'Ethical business practices and transparent partnerships with healthcare providers',
            color: 'from-medical-teal-500 to-medical-teal-600'
        },
        {
            icon: HiLightningBolt,
            title: 'Reliability',
            description: 'Consistent delivery and dependable support for critical healthcare needs',
            color: 'from-medical-green-500 to-medical-green-600'
        },
        {
            icon: HiCheckCircle,
            title: 'Innovation',
            description: 'Cutting-edge solutions and continuous improvement in medical technology',
            color: 'from-purple-500 to-purple-600'
        }
    ]

    const trustMetrics = [
        { icon: HiOfficeBuilding, value: '9+', label: 'Years of Experience', color: 'text-medical-blue-600' },
        { icon: HiCube, value: '500+', label: 'Product Lines', color: 'text-medical-teal-600' },
        { icon: HiUserGroup, value: '100+', label: 'Healthcare Clients', color: 'text-medical-green-600' },
        { icon: HiGlobe, value: '4', label: 'Continents Served', color: 'text-purple-600' }
    ]

    return (
        <section id='about' className="py-20" style={{ backgroundColor: 'rgba(224, 242, 254, 0.7)' }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                        About Alivany Medical Supplies
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        A trusted leader in medical and dental supplies, serving healthcare facilities, organizations, and professionals across Ghana and beyond since 2015.
                    </p>
                    <div className="w-24 h-1 bg-gradient-to-r from-medical-blue-600 to-medical-teal-600 mx-auto mt-6 rounded-full"></div>
                </motion.div>

                {/* Intro Content - Two Column */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
                    {/* Left: Image */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="relative rounded-2xl overflow-hidden shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05),0_0_0_1px_rgba(59,130,246,0.1),0_0_20px_rgba(2,132,199,0.2)]"
                    >
                        <img
                            src={aboutImage}
                            alt="Alivany Medical Supplies - Healthcare Equipment"
                            className="w-full h-full object-cover aspect-[4/3]"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-medical-blue-900/20 to-transparent"></div>
                    </motion.div>

                    {/* Right: About Text */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="space-y-6"
                    >
                        <div className="space-y-5 text-gray-700 leading-relaxed">
                            <p className="text-base md:text-lg lg:text-xl xl:text-2xl">
                                <span className="font-bold text-medical-blue-700">Alivany Medical & Dental Supplies Company Limited</span> is a leading provider of medical supplies to healthcare facilities, organizations, and individuals across Ghana and beyond.
                            </p>

                            <p className="text-base md:text-lg lg:text-xl xl:text-2xl">
                                We serve <span className="font-semibold text-gray-900">hospitals, clinics, health centers, and healthcare professionals</span> with a comprehensive range of high-quality products including surgical instruments, hospital equipment, medical consumables, and laboratory supplies.
                            </p>

                            <p className="text-base md:text-lg lg:text-xl xl:text-2xl">
                                Our commitment to <span className="font-semibold text-medical-teal-700">quality, reliability, and affordability</span> has made us a trusted partner in healthcare delivery. We ensure that every product meets international standards and supports optimal patient care.
                            </p>
                        </div>
                    </motion.div>
                </div>

                {/* Our Mission / Our Story */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="mb-20"
                >
                    <div className="bg-gradient-to-br from-medical-blue-50 via-medical-teal-50 to-medical-green-50 rounded-2xl p-8 md:p-12 border border-medical-blue-100">
                        <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 text-center">
                            Our Mission
                        </h3>
                        <div className="max-w-4xl mx-auto space-y-6 text-gray-700 leading-relaxed">
                            <p className="text-base md:text-lg lg:text-xl xl:text-2xl">
                                Founded in <span className="font-bold text-medical-blue-700">2015</span>, Alivany Medical & Dental Supplies exists to bridge the gap between healthcare facilities and quality medical equipment suppliers.
                            </p>
                            
                            <p className="text-base md:text-lg lg:text-xl xl:text-2xl">
                                We solve the critical challenge of accessing reliable, affordable, and certified medical supplies in Africa. Our mission is to ensure that healthcare providers have access to the tools they need to deliver exceptional patient care.
                            </p>

                            <p className="text-base md:text-lg lg:text-xl xl:text-2xl">
                                We support healthcare delivery by offering:
                            </p>
                            
                            <ul className="list-none space-y-3 pl-0">
                                <li className="flex items-start gap-3 text-base md:text-lg lg:text-xl xl:text-2xl">
                                    <span className="text-medical-blue-600 mt-1">✓</span>
                                    <span>Comprehensive product range from leading international brands in Europe, USA, North America, and Asia</span>
                                </li>
                                <li className="flex items-start gap-3 text-base md:text-lg lg:text-xl xl:text-2xl">
                                    <span className="text-medical-blue-600 mt-1">✓</span>
                                    <span>Biomedical engineering services including calibration, installation, servicing, and maintenance</span>
                                </li>
                                <li className="flex items-start gap-3 text-base md:text-lg lg:text-xl xl:text-2xl">
                                    <span className="text-medical-blue-600 mt-1">✓</span>
                                    <span>Dedicated support for Teaching Hospitals, Private Hospitals, Municipal Hospitals, Health Centers, and Clinics</span>
                                </li>
                                <li className="flex items-start gap-3 text-base md:text-lg lg:text-xl xl:text-2xl">
                                    <span className="text-medical-blue-600 mt-1">✓</span>
                                    <span>One-stop-shop convenience for all medical device needs</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </motion.div>

                {/* Core Values Grid */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="mb-20"
                >
                    <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                        Our Core Values
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {coreValues.map((value, index) => {
                            const Icon = value.icon
                            return (
                                <motion.div
                                    key={value.title}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    className="bg-white rounded-2xl p-6 border-3 border-gray-200 hover:border-medical-blue-300 transition-all duration-300 shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)] hover:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05),0_0_0_1px_rgba(59,130,246,0.15),0_0_10px_rgba(2,132,199,0.2)]"
                                    style={{ borderWidth: '3px' }}
                                >
                                    <div className={`w-14 h-14 bg-gradient-to-br ${value.color} rounded-xl flex items-center justify-center mb-4 shadow-lg`}>
                                        <Icon className="w-7 h-7 text-white" />
                                    </div>
                                    <h4 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h4>
                                    <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
                                </motion.div>
                            )
                        })}
                    </div>
                </motion.div>

                {/* Trust Metrics / Social Proof */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="mb-20"
                >
                    <div className="bg-white rounded-2xl p-8 md:p-12 border-3 border-medical-blue-200 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-2px_rgba(0,0,0,0.05),0_0_0_1px_rgba(59,130,246,0.1),0_0_20px_rgba(2,132,199,0.2)]" style={{ borderWidth: '3px' }}>
                        <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
                            Trusted by Healthcare Professionals
                        </h3>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                            {trustMetrics.map((metric, index) => {
                                const Icon = metric.icon
                                return (
                                    <motion.div
                                        key={metric.label}
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.4, delay: index * 0.1 }}
                                        className="text-center"
                                    >
                                        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-gray-50 to-gray-100 mb-4 ${metric.color}`}>
                                            <Icon className="w-8 h-8" />
                                        </div>
                                        <div className={`text-4xl md:text-5xl font-bold mb-2 ${metric.color}`}>
                                            {metric.value}
                                        </div>
                                        <div className="text-sm font-semibold text-gray-600">
                                            {metric.label}
                                        </div>
                                    </motion.div>
                                )
                            })}
                        </div>
                </div>
                </motion.div>
            </div>
        </section>
    )
}
